// src/hooks/usePromptSuggestions.ts
//
// Subscribes to /users/{uid}/promptSuggestions/current. The doc is
// written by refreshGuide on each guide refresh — same cadence as
// the Live Guide / Discover feed, so suggestions stay in lockstep
// with the user's location and time of day.
//
// Also tracks "have these specific suggestions been seen by the user
// yet?" via localStorage. The chat composer shows a small badge on
// the suggestions button when fresh ones have arrived since the last
// time the user opened the panel.
//
// Important: subscribes to Firebase Auth state changes so the
// Firestore listener attaches as soon as the user is signed in,
// even if the hook mounted before auth resolved (cold start).

import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';

export interface PromptSuggestionsState {
  suggestions: string[];
  /** When the suggestions were last regenerated (server time). */
  generatedAt: Date | null;
  /** True for the brief moment before the first snapshot arrives. */
  loading: boolean;
  /** True when generatedAt is newer than the user's last-seen time. */
  hasUnseen: boolean;
  /** Mark the current suggestions as seen — clears the unseen flag. */
  markSeen: () => void;
}

const SEEN_KEY = (uid: string) => `instantTours:suggestionsSeenAt:${uid}`;

const EMPTY: Omit<PromptSuggestionsState, 'markSeen'> = {
  suggestions: [],
  generatedAt: null,
  loading: true,
  hasUnseen: false,
};

function readSeenAt(uid: string): number {
  try {
    const v = localStorage.getItem(SEEN_KEY(uid));
    return v ? Number(v) : 0;
  } catch {
    return 0;
  }
}

function writeSeenAt(uid: string, ts: number) {
  try {
    localStorage.setItem(SEEN_KEY(uid), String(ts));
  } catch {
    // ignore
  }
}

export function usePromptSuggestions(): PromptSuggestionsState {
  const [base, setBase] = useState(EMPTY);
  const [uid, setUid] = useState<string | null>(
    auth.currentUser?.uid ?? null
  );
  const [seenAt, setSeenAt] = useState<number>(() => {
    const u = auth.currentUser;
    return u ? readSeenAt(u.uid) : 0;
  });

  // Track auth state so the listener attaches once a user is available,
  // even on cold start where the hook mounts before auth resolves.
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      const next = user?.uid ?? null;
      setUid(next);
      if (next) {
        setSeenAt(readSeenAt(next));
      } else {
        // Signed out — clear state and stop showing loading.
        setBase({
          suggestions: [],
          generatedAt: null,
          loading: false,
          hasUnseen: false,
        });
      }
    });
    return unsubAuth;
  }, []);

  // Re-subscribe to Firestore whenever the uid changes.
  useEffect(() => {
    if (!uid) return;

    const ref = doc(db, 'users', uid, 'promptSuggestions', 'current');
    console.info('[usePromptSuggestions] subscribing for uid', uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          console.info('[usePromptSuggestions] doc does not exist yet');
          setBase({
            suggestions: [],
            generatedAt: null,
            loading: false,
            hasUnseen: false,
          });
          return;
        }
        const data = snap.data() as any;
        const list = Array.isArray(data.suggestions)
          ? data.suggestions.filter(
              (s: any) => typeof s === 'string' && s.trim().length > 0
            )
          : [];
        const ts =
          data.generated_at instanceof Timestamp
            ? data.generated_at.toDate()
            : null;
        console.info(
          `[usePromptSuggestions] snapshot: ${list.length} items, generated_at=${ts?.toISOString() || 'null'}`
        );
        setBase({
          suggestions: list,
          generatedAt: ts,
          loading: false,
          hasUnseen: false,
        });
      },
      (err) => {
        console.warn('usePromptSuggestions snapshot error:', err);
        setBase({
          suggestions: [],
          generatedAt: null,
          loading: false,
          hasUnseen: false,
        });
      }
    );

    return unsub;
  }, [uid]);

  const markSeen = useCallback(() => {
    if (!uid) return;
    const now = Date.now();
    writeSeenAt(uid, now);
    setSeenAt(now);
  }, [uid]);

  // Derive hasUnseen at call time so it reflects both fresh server
  // pushes and local markSeen() calls.
  const hasUnseen =
    base.suggestions.length > 0 &&
    !!base.generatedAt &&
    base.generatedAt.getTime() > seenAt;

  return {
    suggestions: base.suggestions,
    generatedAt: base.generatedAt,
    loading: base.loading,
    hasUnseen,
    markSeen,
  };
}
